import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  AcceptAndSendOrder,
  AcceptAndValidateOrder,
  AcceptOrder,
  Cart,
  CartEntity,
  CartError,
  Customer,
  FetchCart,
} from "./cartInterfaces";

import { InsuranceProduct } from "Store/interfaces/products/checkout";
import { BaseProduct } from "Store/interfaces/products/checkout/BaseProduct";
import { toggleProduct } from "Utils/storeUtils";

const initialState: CartEntity = {
  current: null,
  isLoading: false,
  error: null,
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    fetchCartStart: (state, _action: PayloadAction<FetchCart>) => {
      state.isLoading = true;
    },

    fetchCartSucceeded: (state, action: PayloadAction<Cart>) => {
      const { payload } = action;

      const products = payload?.products.map((product) => ({
        ...product,
        visible: true,
      }));

      state.current = { ...payload, products };
      state.error = null;
      state.isLoading = false;
    },

    operationFailed: (state, action: PayloadAction<CartError>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    updateProductStart: (state, _action: PayloadAction<BaseProduct>) => {
      state.isLoading = true;
    },

    removeProductsStart: (state, _action: PayloadAction<BaseProduct[]>) => {
      state.isLoading = true;
    },

    removeProductsSucceeded: (state, action: PayloadAction<BaseProduct[]>) => {
      findAndToggleProducts(action.payload, state);
      removeProducts(action.payload, state);
    },

    undoDeleteProduct: (state, action: PayloadAction<BaseProduct[]>) => {
      findAndToggleProducts(action.payload, state);
    },

    updateProductSucceeded: (
      state,
      action: PayloadAction<InsuranceProduct>,
    ) => {
      updateProduct(action.payload, state);
    },

    updateProductInStoreOnlyStart: (
      state,
      _action: PayloadAction<InsuranceProduct>,
    ) => {
      state.isLoading = true;
    },

    updateProductInStoreOnlySucceeded: (
      state,
      action: PayloadAction<InsuranceProduct>,
    ) => {
      updateProduct(action.payload, state);
    },

    createOrderStart: (state, _action: PayloadAction<{ cartId: string }>) => {
      state.isLoading = true;
    },

    acceptOfferStart: (state, _action: PayloadAction<AcceptOrder>) => {
      state.isLoading = true;
    },

    acceptOfferSucceeded: (state, _action: PayloadAction<string>) => {
      state.isLoading = false;
    },

    acceptOfferAndSendOrderStart: (
      state,
      _action: PayloadAction<AcceptAndSendOrder>,
    ) => {
      state.isLoading = true;
    },

    acceptOfferAndValidateOrderStart: (
      state,
      _action: PayloadAction<AcceptAndValidateOrder>,
    ) => {
      state.isLoading = true;
    },

    acceptOfferAndCreateOrderStart: (
      state,
      _action: PayloadAction<{ cartId: string }>,
    ) => {
      state.isLoading = true;
    },

    validateOrderStart: (state, _action: PayloadAction<string>) => {
      state.isLoading = true;
    },

    sendOrderInsuranceRequestStart: (
      state,
      _action: PayloadAction<Customer>,
    ) => {
      state.isLoading = true;
    },

    sendOrderInsuranceRequestSucceeded: (
      state,
      action: PayloadAction<Cart>,
    ) => {
      const { payload } = action;

      const products = payload?.products.map((product) => ({
        ...product,
        visible: true,
      }));

      state.current = { ...payload, products };
      state.isLoading = false;
    },
  },
});

export const {
  acceptOfferStart,
  acceptOfferSucceeded,
  acceptOfferAndSendOrderStart,
  acceptOfferAndValidateOrderStart,
  acceptOfferAndCreateOrderStart,
  createOrderStart,
  fetchCartStart,
  fetchCartSucceeded,
  operationFailed,
  removeProductsStart,
  removeProductsSucceeded,
  sendOrderInsuranceRequestStart,
  sendOrderInsuranceRequestSucceeded,
  undoDeleteProduct,
  updateProductInStoreOnlyStart,
  updateProductInStoreOnlySucceeded,
  updateProductStart,
  updateProductSucceeded,
  validateOrderStart,
} = cartSlice.actions;
export const cart = cartSlice.reducer;

function updateProduct(
  payload: InsuranceProduct,
  state: {
    current: { products: InsuranceProduct[] | undefined } | null;
    error: any;
    isLoading: boolean;
  },
): void {
  const index = state?.current?.products?.findIndex(
    (product) => product.productId === payload.productId,
  );
  if (index !== undefined && state.current?.products) {
    state.current.products[index] = { ...payload, visible: true };
  }
  state.error = null;
  state.isLoading = false;
}

function removeProducts(
  payload: BaseProduct[],
  state: {
    current: { products: InsuranceProduct[] | undefined } | null;
    error: any;
    isLoading: boolean;
  },
): void {
  payload.forEach((newProduct) => {
    if (state.current?.products) {
      state.current.products = state.current.products.filter(
        (product) => product.productId !== newProduct.productId,
      );
    }
  });
  state.error = null;
  state.isLoading = false;
}

function findAndToggleProducts(
  payload: BaseProduct[],
  state: { current: { products: InsuranceProduct[] | undefined } | null },
): void {
  payload.forEach((newProduct) => {
    const product = state?.current?.products?.find(
      (productFromState) => productFromState.productId === newProduct.productId,
    );
    if (product) {
      toggleProduct(product);
    }
  });
}
